import {
	isUserLogged,
	useIsAdmin,
	useIsDepartmentDirector,
	useIsDepartmentSecretary,
	useIsFacultyAdmin,
	useIsMentorshipAdmin,
	useIsProfessor,
	useIsStudent,
} from "../../utils/utilFunctions";
import { useQuery, useQueryClient } from "react-query";
import { PropsWithChildren, useState } from "react";
import {
	approveInternship,
	deleteCompanyInternship,
	duplicateCompanyInternship,
	invalidateInternship,
	rejectInternship,
} from "../../Requests/company-internship-requests";
import {
	NOTIFICATION_TYPES,
	openNotification,
} from "../Notifications/NotificationsUtils";
import { useTranslation } from "react-i18next";
import { InternshipDTO, InternshipType } from "../../Api";
import {
	getMentorshipOffers,
	getMentorshipsProposalFilters,
} from "../../utils/reactQueriesConstants";
import { MentorshipListProps } from "../../PropsLists/MentorshipListProps";

export const useMentorshipListController = (
	props: PropsWithChildren<MentorshipListProps>
) => {
	const { t } = useTranslation();
	const isAdmin = useIsAdmin();
	const isMentorshipAdmin = useIsMentorshipAdmin();
	const queryClient = useQueryClient();
	const [selectedInternshipId, setSelectedInternshipId] = useState<string>("");
	const [isCompanyTrusted, setIsCompanyTrusted] = useState(false);
	const [markCompleteModalVisibility, setMarkCompleteModalVisibility] =
		useState(false);
	const [deleteModalVisibility, setDeleteModalVisibility] = useState(false);
	const [duplicateModalVisibility, setDuplicateModalVisibility] =
		useState(false);
	const [approveModalVisibility, setApproveModalVisibility] = useState(false);
	const [rejectModalVisibility, setRejectModalVisibility] = useState(false);
	const [spinning, setSpinning] = useState(false);
	const [
		approveInternshipModalVisibility,
		setApproveInternshipModalVisibility,
	] = useState(false);

	const openErrorNotification = (_error: any) => {
		openNotification(
			t("internships.error"),
			t("internships.fetchDatasError"),
			NOTIFICATION_TYPES.ERROR
		);
	};

	const invalidateMentorshipQueries = async () => {
		await queryClient.invalidateQueries(getMentorshipOffers);
		await queryClient.invalidateQueries(getMentorshipsProposalFilters);
	};

	const deleteEntry = () => {
		setSpinning(true);
		deleteCompanyInternship(selectedInternshipId)
			.then(async () => {
				await invalidateMentorshipQueries();
				openNotification(
					t("internships.deleteInternship"),
					t("internships.deleteInternshipSuccess"),
					NOTIFICATION_TYPES.SUCCESS
				);
			})
			.catch((_error) => {
				openNotification(
					t("internships.deleteInternship"),
					t("internships.deleteInternshipError"),
					NOTIFICATION_TYPES.ERROR
				);
			})
			.finally(() => {
				setSpinning(false);
				setDeleteModalVisibility(false);
			});
	};

	const duplicateEntry = () => {
		setSpinning(true);

		duplicateCompanyInternship(selectedInternshipId)
			.then(async () => {
				await invalidateMentorshipQueries();
				openNotification(
					t("internships.duplicateInternship"),
					t("internships.duplicateInternshipSuccess"),
					NOTIFICATION_TYPES.SUCCESS
				);
			})
			.catch((_error) => {
				openNotification(
					t("internships.duplicateInternship"),
					t("internships.duplicateInternshipError"),
					NOTIFICATION_TYPES.ERROR
				);
			})
			.finally(() => {
				setSpinning(false);
				setDuplicateModalVisibility(false);
			});
	};

	const approve = () => {
		setSpinning(true);

		approveInternship(selectedInternshipId)
			.then(async () => {
				await invalidateMentorshipQueries();
				openNotification(
					t("internships.approveInternship"),
					t("internships.approveInternshipSuccess"),
					NOTIFICATION_TYPES.SUCCESS
				);
			})
			.catch((_error) => {
				openNotification(
					t("internships.approveInternship"),
					t("internships.approveInternshipError"),
					NOTIFICATION_TYPES.ERROR
				);
			})
			.finally(() => {
				setSpinning(false);
				setApproveModalVisibility(false);
			});
	};

	const reject = (item: any, description: string) => {
		setSpinning(true);

		rejectInternship(item.id, { message: description })
			.then(async () => {
				await invalidateMentorshipQueries();
				openNotification(
					t("internships.rejectInternship"),
					t("internships.rejectInternshipSuccess"),
					NOTIFICATION_TYPES.SUCCESS
				);
			})
			.catch((_error) => {
				openNotification(
					t("internships.rejectInternship"),
					t("internships.rejectInternshipError"),
					NOTIFICATION_TYPES.ERROR
				);
			})
			.finally(() => {
				setSpinning(false);
				setRejectModalVisibility(false);
			});
	};

	const onChangeCompanyInternshipValidation = (
		checked: boolean,
		item: InternshipDTO
	) => {
		if (checked) {
			approveInternship(item.id)
				.then(async () => {
					await queryClient.invalidateQueries(getMentorshipOffers);
					await queryClient.invalidateQueries(getMentorshipsProposalFilters);
					openNotification(
						t("internships.approveInternship"),
						t("internships.approveInternshipSuccess"),
						NOTIFICATION_TYPES.SUCCESS
					);
				})
				.catch((_error) => {
					openNotification(
						t("internships.approveInternship"),
						t("internships.approveInternshipError"),
						NOTIFICATION_TYPES.ERROR
					);
				});
		} else {
			invalidateInternship(item.id)
				.then(async () => {
					await queryClient.invalidateQueries(getMentorshipOffers);
					await queryClient.invalidateQueries(getMentorshipsProposalFilters);
					openNotification(
						t("internships.rejectInternship"),
						t("internships.rejectInternshipSuccess"),
						NOTIFICATION_TYPES.SUCCESS
					);
				})
				.catch((_error) => {
					openNotification(
						t("internships.rejectInternship"),
						t("internships.rejectInternshipError"),
						NOTIFICATION_TYPES.ERROR
					);
				})
				.finally(() => {
					setSpinning(false);
					setRejectModalVisibility(false);
				});
		}
	};

	return {
		state: {
			selectedInternshipId,
			isCompanyTrusted,
			markCompleteModalVisibility,
			deleteModalVisibility,
			duplicateModalVisibility,
			approveModalVisibility,
			rejectModalVisibility,
			approveInternshipModalVisibility,
			spinning,
		},
		actions: {
			deleteEntry,
			duplicateEntry,
			approve,
			reject,
			onChangeCompanyInternshipValidation,
			setSelectedInternshipId,
			setIsCompanyTrusted,
			setMarkCompleteModalVisibility,
			setDeleteModalVisibility,
			setDuplicateModalVisibility,
			setApproveModalVisibility,
			setApproveInternshipModalVisibility,
		},
	};
};
